/**
 * A strip of chips that narrows the list beneath it — and is also the summary of it.
 *
 * **This is the answer to the operator's complaint, drawn.** Their verdict on the development
 * module was *"I have a whole lot of stuff that I can't even tell what is what"*, and the diagnosis
 * in item 3 of `14-DEVELOPMENT-MODULE-PLAN.md` is that the cause was **the drill-down**: navigating
 * *into* a phase, or off to a separate screen per state, is what loses sight of the whole. A chip
 * strip narrows one list in place. The reader never leaves the page, so they never lose their
 * bearings, and because every chip carries its own count and its own figure, **the strip is the
 * summary as well as the filter** — the two things that used to need two screens.
 *
 * **A dot, never an icon.** There is no natural picture of "Phase 2", so an icon per chip would be
 * decoration that has to be learned. A coloured dot says the one thing a developer wants at a
 * glance — selling, not released, sold out — and costs nothing to read.
 *
 * **Chips are links, not buttons.** The narrowing goes in the address, so it survives a reload, can
 * be sent to a colleague, and is what an Overview figure links to. That last one is not a nicety:
 * item 12 makes every figure on the Overview a link to the list that produced it, and a filter held
 * in a component's state has no address for a figure to point at.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export type ChipTone = "success" | "warn" | "brand" | "muted" | "danger";

/*
  The same five colours `ui.tsx` gives a `Badge`, and deliberately the same mapping.

  `success` is the brand green rather than a green of its own — there is no `success-*` token in
  `index.css`, and a class naming one would silently draw nothing, which is the worst possible
  failure for a dot whose only job is to be a colour.
*/
const DOT: Record<ChipTone, string> = {
  success: "bg-brand-500",
  warn: "bg-warn-500",
  brand: "bg-brand-700",
  muted: "bg-ink-400",
  danger: "bg-danger-500",
};

export interface ChipSpec {
  /** Stable across renders and reorders — a phase id, a stage code, or "all". */
  key: string;
  label: string;
  /** Where pressing it goes. The narrowing lives in the address. */
  to: string;
  /** How many rows this chip would leave. Drawn even when zero, so an empty state is visible. */
  count?: number;
  /**
   * The figure under the label — a price range, or whatever the strip is summarising.
   *
   * Optional because the same control serves the Sales stages, which have no second figure worth
   * carrying: a stage's count is the whole of what it has to say.
   */
  detail?: ReactNode;
  /** Selling · not released · sold out, as a dot. Omitted where the chip has no state. */
  tone?: ChipTone;
  /** What the dot means, for anybody who cannot see a colour. */
  toneLabel?: string;
}

/**
 * The strip.
 *
 * `aria-current="page"` rather than a class alone: which chip is on is the one thing about a filter
 * strip a reader has to know before choosing, and a colour and a weight are nothing at all to a
 * screen reader.
 */
export function Chips({
  chips,
  current,
  label,
  onReorder,
  action,
}: {
  chips: ChipSpec[];
  /** The `key` of the chip that is on. */
  current: string;
  /** What the strip filters, for the landmark: "Phases", "Stage". */
  label: string;
  /**
   * Called when a chip is dragged to a new place, with the keys in their new order.
   *
   * Omitted where the strip does not reorder — the sale stages are a sequence the business defines
   * and dragging them would be meaningless. Where it is given, **the order is the public site's
   * order too**: the operator's decision, because an app and a marketplace that disagree about
   * which phase comes first is a support call nobody can diagnose.
   */
  onReorder?: (keys: string[]) => void;
  /** A control at the end of the strip — "Add a phase". */
  action?: ReactNode;
}) {
  return (
    <div
      className="mb-4 flex flex-wrap items-stretch gap-2"
      role="group"
      aria-label={label}
      data-chips={label}
    >
      {chips.map((chip, index) => {
        const on = chip.key === current;
        return (
          <Link
            key={chip.key}
            to={chip.to}
            aria-current={on ? "page" : undefined}
            data-chip={chip.key}
            /*
              Dragging is offered only where the strip reorders, and the whole chip is the handle.
              A separate grip would be a second target the width of a thumbnail on a control this
              small, and the chip is already the thing somebody points at.
            */
            draggable={onReorder ? true : undefined}
            onDragStart={
              onReorder
                ? (event) => {
                    event.dataTransfer.effectAllowed = "move";
                    /* The index rather than the key: `getData` is unreadable during `dragover` in
                       every browser, so the drop target cannot ask what is being dragged. Setting
                       something is still required or Firefox refuses to start the drag at all. */
                    event.dataTransfer.setData("text/plain", String(index));
                  }
                : undefined
            }
            onDragOver={onReorder ? (event) => event.preventDefault() : undefined}
            onDrop={
              onReorder
                ? (event) => {
                    event.preventDefault();
                    const from = Number(event.dataTransfer.getData("text/plain"));
                    if (!Number.isInteger(from) || from === index) return;
                    const keys = chips.map((row) => row.key);
                    const [moved] = keys.splice(from, 1);
                    keys.splice(index, 0, moved);
                    onReorder(keys);
                  }
                : undefined
            }
            className={`flex min-w-[7rem] flex-col justify-center rounded-[var(--radius-control)] border px-3.5 py-2 text-left transition-colors ${
              on
                ? "border-brand-600 bg-brand-50 text-ink-900"
                : "border-ink-200 bg-white text-ink-600 hover:border-ink-300 hover:bg-ink-50"
            }`}
          >
            <span className="flex items-center gap-2">
              {chip.tone ? (
                <span className={`size-2 shrink-0 rounded-full ${DOT[chip.tone]}`} aria-hidden />
              ) : null}
              <span data-chip-label className={`truncate text-sm ${on ? "font-semibold" : "font-medium"}`}>
                {chip.label}
              </span>
              {typeof chip.count === "number" ? (
                <span
                  data-chip-count
                  className={`tabular ml-auto rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${
                    on ? "bg-brand-100 text-brand-800" : "bg-ink-100 text-ink-500"
                  }`}
                >
                  {chip.count}
                </span>
              ) : null}
            </span>
            {chip.detail || chip.toneLabel ? (
              <span className="mt-0.5 truncate text-[11px] text-ink-500">
                {chip.toneLabel ? <span className="sr-only">{chip.toneLabel}. </span> : null}
                {chip.detail}
              </span>
            ) : null}
          </Link>
        );
      })}
      {action ? <div className="flex items-center">{action}</div> : null}
    </div>
  );
}
